import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  #pointerenterHandler = null;
  #triggerPointerleaveHandler = null;
  timer = null;
  #closeTimer = null;
  #openMode = null; // 'hover' | 'click' | null
  #hoverMoveHandler = null;

  connectedCallback() {
    super.connectedCallback();
    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeDesktopListeners();
    document.removeEventListener('pointermove', this.#hoverMoveHandler);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  #openQuickAddModal() {
    const quickAddComponent = this.querySelector('quick-add-component');
    if (!quickAddComponent) return;
    quickAddComponent.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  #cancelClose = () => {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  #scheduleClose = () => {
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.closeDialog();
    }, 150);
  };

  /**
   * Runs while a HOVER-opened dialog is open. Closes it only when the
   * pointer is truly outside both the trigger and the dialog (with a
   * small buffer), regardless of DOM structure or gaps between them.
   */
  #checkHoverArea = (e) => {
    if (this.#openMode !== 'hover') return;
    const { trigger, dialog } = this.refs;
    if (!dialog.open) return;

    const pad = 16;
    const t = trigger.getBoundingClientRect();
    const d = dialog.getBoundingClientRect();

    const inTrigger =
      e.clientX >= t.left - pad && e.clientX <= t.right + pad && e.clientY >= t.top - pad && e.clientY <= t.bottom + pad;
    const inDialog =
      e.clientX >= d.left - pad && e.clientX <= d.right + pad && e.clientY >= d.top - pad && e.clientY <= d.bottom + pad;

    if (inTrigger || inDialog) {
      this.#cancelClose();
    } else {
      this.#scheduleClose();
    }
  };

  #setupDesktopListeners() {
    const { trigger } = this.refs;

    const pointerenterHandler = () => {
      if (this.refs.dialog.open) return;
      this.timer = setTimeout(() => {
        this.showDialog('hover');
      }, 120);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      this.#pointerenterHandler = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.#cancelClose();
  }

  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;
    const hotspotsContainer = this.parentElement;
    if (!hotspotsContainer) return;

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer?.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';
    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
          y = 'bottom';
        }
      }
    } else {
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Click: opens (or closes, if already open via click) and stays open
   * until an outside click or Escape — independent from hover logic.
   */
  handleHotspotClick = (e) => {
    if (isMobileBreakpoint() || isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      this.#openQuickAddModal();
      return;
    }

    if (this.refs.dialog.open && this.#openMode === 'click') {
      this.closeDialog();
    } else {
      this.showDialog('click');
    }
  };

  showDialog = async (mode = 'hover') => {
    const { dialog } = this.refs;
    this.#cancelClose();
    await this.#calculateDialogPlacement();

    this.#openMode = mode;
    dialog.dataset.showing = 'true';
    dialog.show();

    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);

    if (mode === 'hover') {
      this.#hoverMoveHandler = this.#checkHoverArea;
      document.addEventListener('pointermove', this.#hoverMoveHandler);
    }
  };

  closeDialog = async () => {
    const { dialog } = this.refs;
    this.#cancelClose();
    dialog.dataset.closing = 'true';
    dialog.close();
    this.#openMode = null;

    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);

    if (this.#hoverMoveHandler) {
      document.removeEventListener('pointermove', this.#hoverMoveHandler);
      this.#hoverMoveHandler = null;
    }

    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

customElements.define('product-hotspot-component', ProductHotspotComponent);